<template>
  <FilterChip
    :is-text="showsText"
    :label="chipLabel"
    :icon="icon"
    :tooltip="showLabelOnFace ? null : label"
    :active="active"
    has-popup
    :open="open"
    :pop-title="label"
    :show-clear="active"
    @toggle="open = !open"
    @close="open = false"
    @clear="onClear"
  >
    <FilterRadioList :options="items" :value="value" :aria-label="label" @change="onChange" />
  </FilterChip>
</template>

<script>
import FilterChip from '@/components/filters/FilterChip';
import FilterRadioList from '@/components/filters/FilterRadioList';
import { LABEL_MODE, LABEL_MODES, normalizeLabelMode } from '@/components/filters/label-mode';

/**
 * Generic single-select filter chip: FilterChip shell + FilterRadioList content.
 * Presentational — `:label :icon :items :value` in, `change`(value|null)/`clear` out.
 * Picking a value (or toggling the current one off) closes the popover, like the tenders chip.
 */
export default {
  name: 'FilterSingleChip',
  components: { FilterChip, FilterRadioList },
  inject: { labelled: { from: 'filterChipsLabelled', default: false } },
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: null },
    isText: { type: Boolean, default: false },
    // How the label behaves on a text chip — see label-mode.js. Unknown values clamp to 'full'.
    labelMode: {
      type: String,
      default: LABEL_MODE.FULL,
      validator: (v) => LABEL_MODES.includes(v),
    },
    items: { type: Array, default: () => [] }, // [{ value, text }]
    value: { type: [String, Number, Boolean], default: null },
  },
  data() {
    return { open: false };
  },
  computed: {
    active() {
      return this.value != null && this.value !== '';
    },
    // In a phone's list of filters or line of filters in use (FilterChipsContext), a chip
    // names what it filters, whatever it shows elsewhere.
    showsText() {
      return this.isText || this.labelled;
    },
    // Resolved mode with unknown values clamped to the safe default (always the full label in
    // a phone's list or line of filters in use).
    mode() {
      return this.labelled ? LABEL_MODE.FULL : normalizeLabelMode(this.labelMode);
    },
    // The label sits on the chip face only in 'full' mode — otherwise it moves to the tooltip.
    showLabelOnFace() {
      return this.showsText && this.mode === LABEL_MODE.FULL;
    },
    // The resolved option for the current value — null if it isn't in the list yet (e.g. the
    // option list is still loading on page reload). We must never fall back to the raw value.
    selectedOption() {
      return this.items.find((o) => o.value === this.value) || null;
    },
    chipLabel() {
      if (!this.showsText) return this.label;
      // Not selected, or the option hasn't loaded yet → show the label / icon, never a raw value.
      if (!this.active || !this.selectedOption) return this.mode === LABEL_MODE.VALUE ? '' : this.label;
      return this.mode === LABEL_MODE.FULL ? `${this.label} : ${this.selectedOption.text}` : this.selectedOption.text;
    },
  },
  methods: {
    onChange(next) {
      this.$emit('change', next);
      this.open = false;
    },
    onClear() {
      this.$emit('change', null);
      this.$emit('clear');
      this.open = false;
    },
  },
};
</script>
